import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CreditCardService } from '../../../services/payment/credit-card.service';
import { expirationDateValidator, monthValidator, yearValidator } from '../../../utils/validators';
import { AMEX_CARD_TYPE, UNKNOWN_CARD_TYPE } from './credit-card-info.constants';
import { CARD_TYPES } from '../../../utils/constants';
import { PaymentService } from '../../../services/payment/payment.service';
import { ICreditCardFormGroup } from '../../../interfaces/IPayments';

@Component({
  selector: 'app-credit-card-info',
  templateUrl: './credit-card-info.component.html',
  styleUrl: './credit-card-info.component.scss'
})
export class CreditCardInfoComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear() % 100;
  creditCardForm!: FormGroup<ICreditCardFormGroup>;
  @Input() title: string = 'Card information';
  cardType$: BehaviorSubject<{ type: string; src: string }> = new BehaviorSubject({ type: 'unknown', src: '/assets/logos/credit-card.svg' });
  cleaner$ = new Subject<void>();
  cardTypeSrc: string = '';
  maxLengthCardNumber: number = 16;
  maxLengthCvv: number = 3;
  cardValidityCheckDetails: { number: string; type: string; valid: boolean; cvv: string; cvvValid: boolean } | null = null;


  constructor(private creditCardService: CreditCardService, private paymentService: PaymentService) { }

  ngOnInit(): void {
    this.creditCardForm = this.paymentService.ccForm;
    
    if(this.creditCardForm.controls['cardNumber'].value) {
      this.updateCardValidationWithType();
    }
    
    this.cardType$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: () => {
        this.cardTypeSrc = this.cardType$.value.src;
        this.updateCardNumberValidation();
        this.updateCvvValidation();
      }
    });

    this.creditCardForm.controls['cardNumber'].valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (value) => {
        this.updateCardValidationWithType();
      }
    });

    this.creditCardForm.valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (value) => {
       if (value.cardNumber && value.typeCode && value.cvv) {
        this.cardValidityCheckDetails = this.creditCardService.card(value.cardNumber, value.cvv);
        if (!this.cardValidityCheckDetails.valid) {
          this.creditCardForm.controls['cardNumber'].setErrors({ invalid: true });
        }
       }
      }
    })
  }

  updateCvvValidation(): void {
    if (this.cardType$.value.type === AMEX_CARD_TYPE) {
      this.creditCardForm.controls['cvv'].setValidators([Validators.required, Validators.pattern(/^\d{3,4}$/)]);
      this.maxLengthCvv = 4;
    } else {
      this.creditCardForm.controls['cvv'].setValidators([Validators.required, Validators.pattern(/^\d{3}$/)]);
      this.maxLengthCvv = 3;
    }
  }

  updateCardNumberValidation(): void {
    if (this.cardType$.value.type === UNKNOWN_CARD_TYPE) {
    } else if (this.cardType$.value.type === AMEX_CARD_TYPE) {
      this.creditCardForm.controls['cardNumber'].setValidators([Validators.required, Validators.pattern(/^\d{15}$/)]);
      this.maxLengthCardNumber = 15;
    } else {
      this.creditCardForm.controls['cardNumber'].setValidators([Validators.required, Validators.pattern(/^\d{16}$/)]);
      this.maxLengthCardNumber = 16;
    }
  }

  updateCardValidationWithType(): void {
    if (!this.creditCardForm.controls['cardNumber'].value) { this.cardType$.next(CARD_TYPES[UNKNOWN_CARD_TYPE]); return; }

    const type: string = this.creditCardService.identifyType(this.creditCardForm.controls['cardNumber'].value);
    this.creditCardForm.controls['typeCode'].setValue(type);
    this.cardType$.next(CARD_TYPES[type] || CARD_TYPES[UNKNOWN_CARD_TYPE]);
  }

  formatExpirationDate(event: any) {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    const inputType = event.inputType;
    const isDeleting = inputType === 'deleteContentBackward';

    if (value.length > 2) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
    } else if (value.length === 2 && !isDeleting && !value.includes('/')) {
      value += '/'; // Automatically insert `/` after two digits
    }

    this.creditCardForm.controls['expirationDateInput'].setValue(value, { emitEvent: false });

    if (value.length === 5) {
      const [month, year] = value.split('/');
      const fullYear = `20${year}`;

      this.creditCardForm.controls['expirationMonth'].setValue(month);
      this.creditCardForm.controls['expirationYear'].setValue(fullYear);
    } else {
      this.creditCardForm.controls['expirationMonth'].setValue('');
      this.creditCardForm.controls['expirationYear'].setValue('');
    }
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
